import axios from 'axios';
import { getToken } from '../utils/auth';
import { API_BASE_URL } from '../config/runtime';

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
});

// 요청 인터셉터 - 토큰 자동 추가
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 서비스 타입 정의
export interface ServicePackage {
  price: number;
  delivery_days: number;
  revisions: number;
  features: string[];
}

export interface ServiceData {
  title: string;
  category: string;
  subcategory?: string;
  description: string;
  package_basic: ServicePackage;
  package_standard?: ServicePackage;
  package_premium?: ServicePackage;
  portfolio_images?: string[];
  tags?: string[];
  faqs?: Array<{ question: string; answer: string }>;
  requirements?: string;
  source_files_included?: boolean;
  commercial_use?: boolean;
}

// 카테고리 매핑 (문자열 -> ID)
const categoryMap: { [key: string]: number } = {
  'design': 1,
  'development': 2,
  'marketing': 3,
  'writing': 4,
  'video': 5,
  'music': 6,
  'business': 7,
  'other': 8,
};

// Service API
export const serviceAPI = {
  // 서비스 생성
  createService: async (serviceData: ServiceData) => {
    console.log('API: 서비스 생성 요청', serviceData);
    
    // category를 category_id로 변환
    const category_id = categoryMap[serviceData.category] || 2; // 기본값: development
    
    // 백엔드 형식으로 데이터 변환
    const backendData = {
      category_id,
      title: serviceData.title,
      description: serviceData.description,
      short_description: serviceData.description.substring(0, 200),
      
      // 베이직 패키지 (필수)
      price_basic: serviceData.package_basic.price,
      delivery_days_basic: serviceData.package_basic.delivery_days,
      revisions_basic: serviceData.package_basic.revisions,
      
      // 스탠다드 패키지 (선택)
      price_standard: serviceData.package_standard?.price,
      delivery_days_standard: serviceData.package_standard?.delivery_days,
      revisions_standard: serviceData.package_standard?.revisions,
      
      // 프리미엄 패키지 (선택)
      price_premium: serviceData.package_premium?.price,
      delivery_days_premium: serviceData.package_premium?.delivery_days,
      revisions_premium: serviceData.package_premium?.revisions,
      
      // 추가 정보
      portfolio_images: serviceData.portfolio_images,
      service_includes: serviceData.package_basic.features,
      skills: serviceData.tags,
      tags: serviceData.tags,
      faq: serviceData.faqs,
      requirements: serviceData.requirements,
      source_files_included: serviceData.source_files_included ? 1 : 0,
      commercial_use: serviceData.commercial_use ? 1 : 0,
    };
    
    console.log('API: 백엔드로 전송할 데이터', backendData);
    const response = await api.post('/services', backendData);
    console.log('API: 서비스 생성 응답', response.data);
    return response.data;
  },

  // 서비스 목록 조회
  getServices: async (params?: {
    category?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: string;
    page?: number;
    limit?: number;
  }) => {
    console.log('API: 서비스 목록 조회', params);
    const response = await api.get('/services', { params });
    console.log('API: 서비스 목록 응답', response.data);
    return response.data;
  },

  // 서비스 상세 조회
  getServiceById: async (id: number) => {
    console.log('API: 서비스 상세 조회', id);
    const response = await api.get(`/services/${id}`);
    console.log('API: 서비스 상세 응답', response.data);
    return response.data;
  },

  // 판매자별 서비스 조회
  getServicesBySeller: async (nickname: string) => {
    console.log('API: 판매자별 서비스 조회', nickname);
    const response = await api.get(`/services/seller/${nickname}`);
    console.log('API: 판매자별 서비스 응답', response.data);
    return response.data;
  },

  // 사용자 ID로 서비스 조회 (내 서비스 목록)
  // 보안: nickname을 URL에 전송하지만 백엔드에서는 JWT의 user ID를 사용
  getServicesByUserId: async (userId: number, nickname: string) => {
    console.log('API: 사용자 ID로 서비스 조회', { userId, nickname });
    // nickname을 URL에 포함하지만, 백엔드는 JWT 토큰의 user ID로 조회
    const response = await api.get(`/services/freelancer/${nickname}`);
    console.log('API: 사용자 서비스 응답', response.data);
    return response.data;
  },

  // 서비스 수정
  updateService: async (id: number, serviceData: Partial<ServiceData>) => {
    console.log('API: 서비스 수정 요청', { id, serviceData });
    const response = await api.put(`/services/${id}`, serviceData);
    console.log('API: 서비스 수정 응답', response.data);
    return response.data;
  },

  // 서비스 삭제
  deleteService: async (id: number) => {
    console.log('API: 서비스 삭제 요청', id);
    const response = await api.delete(`/services/${id}`);
    console.log('API: 서비스 삭제 응답', response.data);
    return response.data;
  },

  // 서비스 카테고리 목록
  getCategories: () => {
    return [
      { value: 'design', label: '디자인' },
      { value: 'development', label: '개발/프로그래밍' },
      { value: 'marketing', label: '마케팅' },
      { value: 'writing', label: '글쓰기/번역' },
      { value: 'video', label: '영상/사진' },
      { value: 'music', label: '음악/오디오' },
      { value: 'business', label: '비즈니스 컨설팅' },
      { value: 'other', label: '기타' },
    ];
  },
};

export default api;
